import {
  formatPrice,
  formatTimeFromMinutes,
  HOUR,
  MINUTE,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/compat/router';
import { usePathname } from 'next/navigation';
import { type FC, useCallback, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  type HourlyOrderInput,
  type Scalars,
  SubscriptionInterval,
} from '../../../../__generated-gql-types__/globalTypes';
import GiftIllustration from '../../../../assets/svgs/gift-illustration.svg';
import {
  BUY_COACHING_REGISTER_OPTIONS,
  type LocalCheckoutSessionInputs,
  setLocalCheckoutOrderInputs,
  setLocalCheckoutSessionInputs,
} from '../../../../utils/checkout';
import { DEFAULT_ERROR_MESSAGE } from '../../../../utils/constants';
import {
  LelandSessionStorage,
  PREVIOUS_ROUTE_KEY,
} from '../../../../utils/storage';
import LoadingIndicator from '../../../common/LoadingIndicator';
import { ErrorMessage } from '../../../forms/Inputs';
import HourMinuteInput from '../../../inputs/HourMinuteInput';
import {
  SelectHoursInput,
  useSelectHoursInput,
} from '../../../inputs/SelectHoursInput';

import {
  type BookCoachingTimeFormCoachFragment,
  type BookCoachingTimeFormCoachingFocusFragment,
  type BookCoachingTimeFormCreateOrderMutationVariables,
  useBookCoachingTimeFormInitialLoadQuery,
} from './__generated-gql-types__/BookCoachingTimeForm.generated';

interface BookCoachingTimeFormProps {
  categoryUrn: Possible<Scalars['CategoryUrn']['output']>;
  coach: BookCoachingTimeFormCoachFragment;
  defaultValues: BookCoachingTimeFormCreateOrderMutationVariables;
  orderUrn?: Scalars['OrderUrn']['output'];
  services: Possible<BookCoachingTimeFormCoachingFocusFragment[]>;
  sessionToSchedule?: Possible<LocalCheckoutSessionInputs>;
  justThisSessionTime?: number;
  justThisSessionPrice?: number;
  orderToAssignId?: string;
}

export const BookCoachingTimeForm: FC<BookCoachingTimeFormProps> = ({
  categoryUrn,
  coach,
  defaultValues,
  sessionToSchedule,
  justThisSessionTime,
  justThisSessionPrice,
  orderToAssignId,
}) => {
  const [selectedTime, setSelectedTime] = useState<
    Nullable<SubscriptionInterval.MONTHLY | SubscriptionInterval.ONE_TIME>
  >(SubscriptionInterval.ONE_TIME);
  const [selectedMonthlySku, setSelectedMonthlySku] =
    useState<Nullable<string>>(null);
  const [selectedOneTimeOption, setSelectedOneTimeOption] =
    useState<Nullable<number>>(null);

  const router = useRouter();
  const pathname = usePathname();

  const tier = coach.tier;

  const { data: initialLoadData, loading: initialLoading } =
    useBookCoachingTimeFormInitialLoadQuery({
      variables: { tier: tier, period: selectedTime, coachUrn: coach.id },
    });
  const singleHourData = initialLoadData?.singleHourSku;
  const isSubscriber = !!initialLoadData?.activeCustomerSubscriptions.length;
  const subscriptionSkuUnitPrice = initialLoadData?.activeCustomerSubscriptions
    ?.filter((s) => s.useSkuUnitPrice)
    ?.at(0)?.sku.unitPrice;

  const { hoursOfCoachingOptions } = useSelectHoursInput({
    tier: coach.tier,
    period: selectedTime,
    justThisSessionTime,
    justThisSessionPrice,
    singleHourPrice: singleHourData?.unitPrice ?? 0,
  });

  const { register, setValue, handleSubmit, watch } =
    useForm<BookCoachingTimeFormCreateOrderMutationVariables>({
      defaultValues,
    });

  register(
    'hourlyOrder.timeInMinute',
    BUY_COACHING_REGISTER_OPTIONS.TIME_IN_MINUTE,
  );

  const watchTimeInMinute = watch('hourlyOrder.timeInMinute');

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

  useEffect(() => {
    if (isSubscriber) {
      setSelectedTime(SubscriptionInterval.ONE_TIME);
    }
  }, [isSubscriber]);

  useEffect(() => {
    if (hoursOfCoachingOptions.length > 0) {
      // Default to the top option
      const defaultOption = hoursOfCoachingOptions[0].value;

      if (
        selectedTime === SubscriptionInterval.ONE_TIME &&
        !selectedOneTimeOption
      ) {
        setSelectedOneTimeOption(defaultOption as number);
        setValue('hourlyOrder.timeInMinute', defaultOption as number);
      } else if (
        selectedTime === SubscriptionInterval.MONTHLY &&
        !selectedMonthlySku
      ) {
        setSelectedMonthlySku(defaultOption as string);
      }
    }
  }, [
    selectedTime,
    hoursOfCoachingOptions,
    setValue,
    selectedOneTimeOption,
    selectedMonthlySku,
  ]);

  const onSubmitWrapper: SubmitHandler<BookCoachingTimeFormCreateOrderMutationVariables> =
    useCallback(
      async (values) => {
        try {
          const localCategoryUrn =
            categoryUrn ?? values.categoryUrn ?? defaultValues.categoryUrn;

          if (tier) {
            const hourlyInput: Optional<HourlyOrderInput> =
              selectedTime === SubscriptionInterval.ONE_TIME
                ? {
                    timeInMinute: watchTimeInMinute,
                  }
                : undefined;

            const skuInput: Optional<Scalars['SkuUrn']['output']> =
              selectedTime === SubscriptionInterval.MONTHLY &&
              selectedMonthlySku
                ? selectedMonthlySku
                : undefined;

            setLocalCheckoutOrderInputs({
              coachUrn: coach.id,
              tier,
              hourlyOrder: hourlyInput,
              subscriptionOrder: skuInput,
              categoryId: localCategoryUrn,
              unassignedOrderId: orderToAssignId,
            });

            LelandSessionStorage.setItem(PREVIOUS_ROUTE_KEY, pathname);

            if (sessionToSchedule) {
              setLocalCheckoutSessionInputs(sessionToSchedule);
            }

            await router?.push({
              pathname: '/checkout',
              query: {
                orderToAssign: orderToAssignId,
              },
            });
          }
        } catch (e) {
          setErrorMessage(DEFAULT_ERROR_MESSAGE);
          console.warn(e);
        }
      },
      [
        categoryUrn,
        defaultValues.categoryUrn,
        tier,
        selectedTime,
        watchTimeInMinute,
        selectedMonthlySku,
        coach.id,
        orderToAssignId,
        router,
        sessionToSchedule,
      ],
    );

  return initialLoading ? (
    <div className="p-6">
      <LoadingIndicator iconClassName="w-20 h-20" />
    </div>
  ) : (
    <form
      id="book-coaching-time-form"
      className="flex grow flex-col overflow-hidden"
      onSubmit={handleSubmit(onSubmitWrapper)}
    >
      <div className="flex grow flex-col justify-start overflow-y-auto">
        <div className="p-6">
          <>
            <h3 className="mb-6 text-lg font-medium text-leland-gray-dark sm:block">
              Select a quantity of coaching time
            </h3>
            {isSubscriber ? null : (
              <div className="mb-4 grid w-full grid-cols-2 gap-4">
                <button
                  className={`flex flex-col rounded-md border p-4 text-start ${
                    selectedTime === SubscriptionInterval.MONTHLY
                      ? 'border-leland-gray-dark shadow-border shadow-leland-gray-dark'
                      : ''
                  }`}
                  type="button"
                  onClick={() => {
                    setSelectedTime(SubscriptionInterval.MONTHLY);
                    setSelectedOneTimeOption(watchTimeInMinute);
                    setValue('hourlyOrder.timeInMinute', 0);
                  }}
                >
                  <div className="flex justify-between">
                    <div className="flex justify-between gap-4 text-left">
                      <h3 className="text-lg font-medium text-leland-gray-dark">
                        Monthly membership
                      </h3>
                      <div className="hidden items-center justify-center rounded bg-leland-primary/25 px-1.5 py-1 text-sm font-medium text-leland-dark-green sm:flex">
                        Best pricing
                      </div>
                    </div>
                  </div>
                  <div className="mt-1 text-leland-gray-light">
                    Includes Leland+ and other perks
                  </div>
                  <div className="mt-4 flex items-center justify-center rounded bg-leland-primary/25 px-1.5 py-1 text-sm font-medium text-leland-dark-green sm:hidden">
                    Best pricing
                  </div>
                </button>
                <button
                  type="button"
                  className={`flex flex-col rounded-md p-4 text-start ${
                    selectedTime === SubscriptionInterval.ONE_TIME
                      ? 'border-2 border-leland-gray-dark p-4'
                      : 'border p-[17px]'
                  }`}
                  onClick={() => {
                    setSelectedTime(SubscriptionInterval.ONE_TIME);
                    setSelectedMonthlySku(selectedMonthlySku);
                    if (selectedOneTimeOption) {
                      setValue(
                        'hourlyOrder.timeInMinute',
                        selectedOneTimeOption,
                      );
                    } else {
                      const defaultOption =
                        hoursOfCoachingOptions.find(
                          (option) => typeof option.value === 'number',
                        )?.value ?? null;
                      setSelectedOneTimeOption(defaultOption as number);
                      setValue(
                        'hourlyOrder.timeInMinute',
                        defaultOption as number,
                      );
                    }
                  }}
                >
                  <h3 className="text-lg font-medium text-leland-gray-dark">
                    One-time purchase
                  </h3>
                  <p className="mt-1 text-leland-gray-light">
                    No extra perks included
                  </p>
                </button>
              </div>
            )}
          </>
          {isSubscriber && subscriptionSkuUnitPrice ? (
            <>
              <p>
                This time will be added to your balance with{' '}
                {coach.user.firstName}
              </p>
              <HourMinuteInput
                className="mt-4 w-full"
                valueInMinutes={watchTimeInMinute ?? 0}
                max={(100 * HOUR) / MINUTE}
                onChange={(timeInMinute) =>
                  setValue('hourlyOrder.timeInMinute', timeInMinute)
                }
              />
              <div className="mt-4 flex w-full flex-row justify-between pb-10">
                <p>Total</p>
                <p>
                  {formatTimeFromMinutes(watchTimeInMinute)} for{' '}
                  {formatPrice(
                    (watchTimeInMinute / 60.0) * subscriptionSkuUnitPrice,
                  )}
                </p>
              </div>
            </>
          ) : (
            <>
              <SelectHoursInput
                onChange={(val) =>
                  selectedTime === SubscriptionInterval.ONE_TIME
                    ? setValue('hourlyOrder.timeInMinute', val as number)
                    : setSelectedMonthlySku(val as string)
                }
                value={
                  selectedTime === SubscriptionInterval.ONE_TIME
                    ? watchTimeInMinute
                    : selectedMonthlySku
                }
                hoursOfCoachingOptions={hoursOfCoachingOptions}
              />
              {selectedTime === SubscriptionInterval.MONTHLY ? (
                <div className="mt-4 flex gap-6 rounded border p-5">
                  <p className="grow text-leland-gray-light">
                    <span className="font-medium text-leland-gray-dark">
                      Your subscription includes perks.{' '}
                    </span>
                    Access Leland+ -- a library of videos, templates, and
                    examples curated by top coaches. Additionally, get
                    discounts, exclusive events, matching support, and more.
                  </p>
                  <GiftIllustration className="size-7 shrink-0" />
                </div>
              ) : null}
            </>
          )}
        </div>

        {errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </div>
    </form>
  );
};
